import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LoginForm from "@/components/auth/LoginForm";
import SignupForm from "@/components/auth/SignupForm";
import { motion } from "framer-motion";
import { UserPlus, LogIn, CheckCircle2 } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSelector, useDispatch } from "react-redux";
import {
  resetGoogleSignInState,
  resetLoading,
  setShowVerification,
  setVerificationEmail,
} from "../store/auth/authSlice";
import GoogleRoleSelectionStep from "./auth/RoleSelectionStep";

const AuthDialogContent = ({
  defaultTab,
  onAuthSuccess,
  redirectPage,
  onCancel,
  onGoogleAuth,
}) => {
  const dispatch = useDispatch();
  const {
    requiresRoleSelection,
    isLoading,
    showVerification,
    verificationEmail,
  } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState(defaultTab || "login");

  const handleBackToAuth = () => {
    dispatch(resetGoogleSignInState());
  };

  const handleBackToLogin = () => {
    dispatch(resetLoading());
    dispatch(setShowVerification(false));
    dispatch(setVerificationEmail(null));
    setActiveTab("login");
  };

  if (requiresRoleSelection) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col flex-grow p-6"
      >
        <GoogleRoleSelectionStep
          onBack={handleBackToAuth}
          onComplete={onAuthSuccess}
          redirectPage={redirectPage}
        />
      </motion.div>
    );
  }

  if (showVerification) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col flex-grow p-6 items-center justify-center text-center space-y-6"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>

        <div className="space-y-2">
          <DialogTitle className="text-2xl font-bold">
            Check your email
          </DialogTitle>
          <DialogDescription className="text-base">
            We've sent a verification link to your email address{" "}
            {verificationEmail && (
              <span className="font-semibold text-foreground">
                {verificationEmail}
              </span>
            )}
            . Please check your inbox and verify your account to continue.
          </DialogDescription>
        </div>

        <div className="w-full pt-4">
          <Button className="w-full" onClick={handleBackToLogin}>
            Back to Login
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col min-h-0 flex-grow p-6 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
    >
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full flex flex-col flex-grow min-h-0"
      >
        <TabsList className="grid w-full grid-cols-2 bg-gradient-to-br from-muted via-background to-muted/80 p-1.5 rounded-lg shadow-inner mt-4 mb-2 h-auto">
          <TabsTrigger
            value="login"
            className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-md rounded-md transition-all duration-300 ease-out flex items-center gap-2 py-2.5"
          >
            <LogIn size={18} /> Login
          </TabsTrigger>
          <TabsTrigger
            value="signup"
            className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-md rounded-md transition-all duration-300 ease-out flex items-center gap-2 py-2.5"
          >
            <UserPlus size={18} /> Sign Up
          </TabsTrigger>
        </TabsList>

        <div className="w-full pt-4">
          <Button
            variant="outline"
            className="w-full flex items-center gap-2 border rounded-lg py-2.5 shadow-sm hover:bg-accent transition"
            onClick={onGoogleAuth}
            disabled={isLoading}
          >
            <FcGoogle size={20} />
            Continue with Google
          </Button>
        </div>

        <div className="flex items-center gap-2 py-6">
          <div className="flex-grow h-px bg-border"></div>
          <span className="text-xs text-muted-foreground">OR</span>
          <div className="flex-grow h-px bg-border"></div>
        </div>

        <TabsContent value="login" className="flex-grow min-h-0">
          <DialogHeader className="pb-4 pl-0 text-center">
            <DialogTitle className="text-xl font-semibold">
              Welcome Back!
            </DialogTitle>
            <DialogDescription className="text-sm">
              Log in to access your account.
            </DialogDescription>
          </DialogHeader>
          <LoginForm
            onLoginSuccess={onAuthSuccess}
            onCancel={onCancel}
            redirectPage={redirectPage}
          />
        </TabsContent>

        {/* Signup Content */}
        <TabsContent value="signup" className="flex-grow min-h-0">
          <DialogHeader className="pb-4 pl-0 text-center">
            <DialogTitle className="text-xl font-semibold">
              Create Your Account
            </DialogTitle>
            <DialogDescription className="text-sm">
              Join PartsLokator as a Buyer or Seller.
            </DialogDescription>
          </DialogHeader>
          <SignupForm onCancel={onCancel} />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default AuthDialogContent;
